import React from 'react';
import Navbar from '../components/Navbar';
import Breadcrumb from '../components/Breadcrumb';
import ContentSplitSections from '../components/ContentSplitSections';
import { Link } from 'react-router-dom';
import '../styles/landscape.css';
import CTASection from '../components/CTASection';

// Design/Build service data
const designBuildServices = [
  {
    title: "Landscape Design",
    text: `Your Vision, Our Expertise\n\nEvery property has its own potential and we're here to help you bring that to life. Our design process starts and needs with listening. Understanding how you use your space, how you want it to feel, and what will make it last is key to our success.\n\nWe tailor each plan to their unique property needs and functionality, blending creativity with practicality. From classic lawns to naturalized, water-wise landscapes, every project is designed for beauty, function, and longevity.\n\nDesign Considerations:\n• Microclimate and site analysis\n• Low-maintenance plant selection\n• Efficient irrigation planning\n• Snow and water flow management\n• Wildlife and pet-friendly design`,
    image: "/images/blueLand.jpg",
    alt: "Custom landscape design plans",
    reverse: false
  },
  {
    title: "Professional Installation",
    text: `From Plans to Places\n\nOur installation teams bring decades of experience and the same attention to detail to every project- whether it's a small courtyard or a full estate.\n\nWe partner closely with contractors, architects, and homeowners to ensure every element comes together seamlessly.\n\nInstallation Highlights:\n• Soil and grading preparation\n• Precision plant placement\n• Drainage and irrigation integration\n• Expert hardscape coordination`,
    image: "/images/landscape/treeInstallIsland1.jpeg",
    alt: "Professional landscape installation",
    reverse: true
  },
  {
    title: "Sustainable Practices",
    text: `Landscapes That Last\n\nWe believe sustainability is simply a good design. By selecting adaptive plants, planning water efficiency, and minimizing chemical inputs, we create outdoor environments that thrive naturally and require less overtime.\n\nSustainability Features:\n• Water-wise irrigation systems\n• Reduced chemical use\n• Native and adaptive plantings\n• Integrated pest management\n• Kid, trout, and pet-safe practices`,
    image: "/images/landscape/frontLawn.png",
    alt: "Sustainable landscape practices",
    reverse: false
  }
];

// Enduring Hardscape Services data
const enduringHardscapeServices = [
  {
    title: "Paver Installations",
    text: `Precision Meets Durability\n\nFrom driveways to patios, our paver systems combine aesthetic precision with practical longevity. Our crews prepare every base with care to prevent settling, frost movement, and wear. The result: surfaces that stay smooth, strong, and timeless.\n\nApplications include:\n• Driveways and parking areas\n• Walkways and garden paths\n• Pool decks and gathering spaces\n• Permeable and decorative paver systems`,
    image: "/images/hardscape/HomePaverCover1.jpg",
    alt: "Professional paver installation",
    reverse: false
  },
  {
    title: "Retaining Walls & Structures",
    text: `Engineering with Artistry\n\nWe build walls that do more than retain — they define and connect spaces. Whether it's a terraced garden, a seat wall around a patio, or a structural hillside wall, each installation is engineered for integrity and built with visual harmony.\n\nWall Types:\n• Engineered retaining walls\n• Natural boulder or rock walls\n• Decorative seat and privacy walls\n• Terraced gardens and steps`,
    image: "/images/hardscape/retainingWall3.jpeg",
    alt: "Stone retaining wall construction",
    reverse: true
  },
  {
    title: "Natural Stone Masonry",
    text: `Timeless, Natural, Enduring\n\nOur masons hand-select every stone to complement your home and landscape. From flagstone patios to natural rock retainment walls, each element is crafted to age gracefully, reflecting the character of the Wood River Valley.\n\nPopular Applications:\n• Flagstone patios and walkways\n• Stone steps and garden borders\n• Natural rock retaining walls\n• Water feature surroundings`,
    image: "/images/hardscape/waterfall.jpg",
    alt: "Natural stone patio installation",
    reverse: false
  }
];

// Outdoor Living Spaces data
const outdoorLivingSpaces = [
  {
    title: "Fire Features",
    text: `Gather Around the Glow\n\nA fire brings people together. From rustic stone pits to modern gas fireplaces, we create custom fire features that become the heart of your outdoor environment. Every installation is designed for safety, comfort, and connection.\n\nOptions include:\n• Natural stone fire pits\n• Gas or wood-burning fireplaces\n• Cooking and entertaining features\n• Integrated seating and surroundings`,
    image: "/images/fire/customPitNight.jpg",
    alt: "Custom fire pit installation",
    reverse: false
  },
  {
    title: "Outdoor Living",
    text: `Designed for Connection, Built for Every Season\n\nYour outdoor space should feel like an extension of your home — a place to gather, cook, or unwind beneath Idaho's open sky. We design and build complete outdoor living environments that balance comfort, beauty, and durability. Each space is customized to your lifestyle and crafted to perform through the Valley's changing seasons.\n\nCommon Features:\n• Outdoor kitchens and bar seating\n• Lounge areas and built-in seating walls\n• Dining patios and pergola structures\n• Integrated lighting and heating elements\n• Storage and weather-resistant finishes`,
    image: "/images/hardscape/homeHardscape1.jpg",
    alt: "Luxury outdoor kitchen design",
    reverse: true
  }
];

// Irrigation Systems data
const irrigationServices = [
  {
    title: "Irrigation Systems",
    text: `Precision Water Management\n\nEfficient irrigation is the foundation of a healthy, sustainable landscape. We design and install custom systems tailored to your property's unique needs, ensuring every plant receives the right amount of water while minimizing waste.\n\nWe believe sustainability is simply good design. By planning water efficiency and integrating smart technology, we create systems that thrive naturally and require less maintenance over time.\n\nOur Services Include:\n• Custom system design and planning\n• Professional installation and integration\n• Water-efficient technology\n• Smart controller integration\n• Zone-based watering for optimal efficiency\n• Snow and water flow management`,
    image: "/images/water/creekPretty1.jpg",
    alt: "Professional irrigation system installation",
    reverse: false
  }
];

const LandscapeConstruction = () => {

  return (
    <div className="landscape-page">
      <Navbar />
      <Breadcrumb customLabel="Landscape Construction" />
      {/* Hero Section */}
      <section
        className="landscape-hero">
        <img
          src="/images/landscape/LandscapeHero5.jpg"
          alt="Beautiful Landscape Construction"
          className="hero-image"
          loading="eager"
          decoding="async"
        />
        <div className="landscape-hero-content">
          <h1 className="landscape-hero-title">Building Outdoor Spaces That Last</h1>
          <p className="landscape-hero-subtitle">
            From landscape design and installation to hardscapes and irrigation systems — we create outdoor environments that combine beauty, function, and durability.
          </p>
          <div className="hero-buttons">
            <Link to="mailto:info@allseasons.info">
                     <button className="btn-cta"
                     target="_blank"
                     rel="noopener noreferrer" style={{ textTransform: "uppercase" }}>
                       Request a Consultation
                     </button>
                     </Link>
            <Link to="/gallery/landscape" className="btn-primary">Gallery</Link>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="section bg-white">
        <div className="section-inner">
          <div className="intro-content">
            <h2 className="section-title text-center">What We Build</h2>
            <p className="intro-text">
             At All Seasons, we don't just install landscapes — we build outdoor spaces that grow with you. Every project is designed around your property, your lifestyle, and Idaho's changing seasons. From landscape design and installation to custom hardscapes and efficient irrigation systems, our team balances beauty and function to create landscapes that last.
            </p>
          </div>
        </div>
      </section>

      {/* Design & Build Excellence Section */}
      <section className="section bg-white section-divider-editorial">
        <div className="section-inner">
          <h2 className="section-title text-center">Design & Build Excellence</h2>
          <p className="section-subtitle text-center">Where creativity meets expertise to transform your outdoor spaces</p>
        </div>
      </section>

      <ContentSplitSections sections={designBuildServices} />

      {/* Gallery CTA */}
      <section className="section bg-white">
        <div className="section-inner text-center">
          <Link to="/Gallery/landscape" className="btn-primary">
            View Our Work
          </Link>
        </div>
      </section>

      {/* Enduring Hardscape Services Section */}
      <section className="section bg-white section-divider-editorial">
        <div className="section-inner">
          <h2 className="section-title text-center">Enduring Hardscape Services</h2>
          <p className="section-subtitle text-center">We design and build landscape features that enhance the beauty and the purpose of your property. Each project blends craftmanship and engineering to feature unique stones, pavers and structures designed to sustain Idaho's seasons.</p>
        </div>
      </section>

      <ContentSplitSections sections={enduringHardscapeServices} />

      {/* Gallery CTA */}
      <section className="section bg-white">
        <div className="section-inner text-center">
          <Link to="/Gallery/hardscape" className="btn-primary">
            View Our Work
          </Link>
        </div>
      </section>

      {/* Outdoor Living Spaces Section */}
      <section className="section bg-white section-divider-editorial">
        <div className="section-inner">
          <h2 className="section-title text-center">Outdoor Living Spaces</h2>
          <p className="section-subtitle text-center">Outdoor spaces should feel as inviting as the rooms inside your home. From fire pits and relaxation to kitchen spaces and hosting friends, we design and build spaces made to live in and to love, season after season.</p>
        </div>
      </section>

      <ContentSplitSections sections={outdoorLivingSpaces} />

      {/* Gallery CTA */}
      <section className="section bg-white">
        <div className="section-inner text-center">
          <Link to="/Gallery/hardscape" className="btn-primary">
            View Our Work
          </Link>
        </div>
      </section>

      {/* Irrigation Systems Section */}
      <section className="section bg-white section-divider-editorial">
        <div className="section-inner">
          <h2 className="section-title text-center">Irrigation Systems</h2>
          <p className="section-subtitle text-center">Efficient water management for healthy, sustainable landscapes</p>
        </div>
      </section>

      <ContentSplitSections sections={irrigationServices} />

      {/* Gallery CTA */}
      <section className="section bg-white">
        <div className="section-inner text-center">
          <Link to="/Gallery/water" className="btn-primary">
            View Our Work
          </Link>
        </div>
      </section>

      {/* CTA Section */}
     <CTASection
  title="READY TO START YOUR PROJECT?"
  subtitle="FROM DESIGN CONSULTATION TO FINAL INSTALLATION, WE'RE HERE TO BRING YOUR VISION TO LIFE"
  galleryLink="/Gallery/landscape"
  showEmergency={false}
/>
    </div>
  );
};

export default LandscapeConstruction;
